import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./Reviews.module.sass";
import Icon from "../Icon";
import { getAuthToken, getUserData, decodeToken } from "../../utils/authHelpers";
import { authFetch } from "../../utils/authFetch";
import ModalPortal from "../Modal/ModalPortal";
import CreatorSearchAutocomplete from "../CreatorSearchAutocomplete";

const Reviews = ({ className }) => {
    const [loading, setLoading] = useState(true);
    const [reviewsByYou, setReviewsByYou] = useState([]);
    const [showAddReviewModal, setShowAddReviewModal] = useState(false);
    const [editingReview, setEditingReview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        // Fetch reviews
        fetchReviews();
    }, []);

    // Fetch creators function for the autocomplete component
    const fetchCreators = useCallback(async (searchTerm = '') => {
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const url = `${apiBase}/api/catalogue/creators`;
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    page: 1,
                    limit: 50, // Limit results for autocomplete
                    search: searchTerm
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                
                if (data.ok && data.creators && Array.isArray(data.creators)) {
                    // Return creators with all necessary fields
                    return data.creators.map(creator => ({
                        id: creator.id,
                        creator_profile_id: creator.id,
                        first_name: creator.first_name,
                        last_name: creator.last_name,
                        handle: creator.handle,
                        avatar_url: creator.avatar_url,
                        avatar_asset_url: creator.avatar_asset_url,
                        location_city: creator.location_city,
                        location_state: creator.location_state,
                        bio: creator.bio
                    }));
                }
            }
            return [];
        } catch (error) {
            console.error('Error fetching creators:', error);
            return [];
        }
    }, []);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const token = getAuthToken() || localStorage.getItem('postaway_session');
            const userData = getUserData();
            const email = userData?.email || localStorage.getItem('postaway_email') || 
                         (token ? decodeToken(token)?.email : null);
            
            if (!email) {
                setLoading(false);
                return;
            }
            
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            
            // Fetch reviews by this host
            const response = await fetch(`${apiBase}/api/reviews/by-host?email=${encodeURIComponent(email)}`);
            if (response.ok) {
                const data = await response.json();
                if (data.ok && data.reviews) {
                    setReviewsByYou(data.reviews.map(r => ({
                        ...r,
                        author: r.creator_name,
                        content: r.review_text,
                        time: new Date(r.created_at).toLocaleDateString(),
                        avatar: r.creator_avatar_url
                    })));
                }
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddReview = async (reviewData) => {
        setSubmitting(true);
        const isEditing = !!editingReview;
        try {
            const token = getAuthToken();
            
            if (!token) {
                alert('Please log in to add a review');
                return;
            }

            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            
            const response = await authFetch(`${apiBase}/api/reviews/add-creator-review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reviewData)
            });
            
            if (response.ok) {
                setShowAddReviewModal(false);
                setEditingReview(null);
                alert(isEditing ? 'Review updated successfully!' : 'Review added successfully!');
                fetchReviews();
            } else {
                const errorText = await response.text();
                console.error('Failed to save review:', response.status, errorText);
                
                let errorMessage = isEditing ? 'Failed to update review' : 'Failed to add review';
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (e) {
                    errorMessage = errorText || errorMessage;
                }
                
                alert(errorMessage);
            }
        } catch (error) {
            if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
                console.error('Error saving review:', error);
                alert(isEditing ? 'Failed to update review' : 'Failed to add review');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditReview = (review) => {
        setEditingReview(review);
        setShowAddReviewModal(true);
    };

    if (loading) {
        return (
            <div className={cn(className, styles.reviews)}>
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: '#666'
                }}>
                    Loading reviews...
                </div>
            </div>
        );
    }

    const totalReviews = reviewsByYou.length;

    return (
        <>
        <div className={cn(className, styles.reviews)}>
            <div className={styles.head}>
                <div className={styles.title}>
                    {totalReviews > 0 ? `${totalReviews} review${totalReviews !== 1 ? 's' : ''}` : 'Reviews'}
                </div>
                <div className={styles.nav}>
                    <button
                        className={cn("button-small", styles.addButton)}
                        onClick={() => setShowAddReviewModal(true)}
                        style={{
                            padding: '6px 16px',
                            background: '#0f084b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            height: '28px',
                            lineHeight: '1'
                        }}
                    >
                        <Icon name="plus" size="12" />
                        Add Creator Review
                    </button>
                </div>
            </div>
            <div className={styles.group}>
                {reviewsByYou.length > 0 ? (
                    reviewsByYou.map((x, index) => (
                    <div className={styles.item} key={index}>
                        <div className={styles.avatar}>
                            {x.avatar ? (
                                <img src={x.avatar} alt="Avatar" />
                            ) : (
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    color: '#666'
                                }}>
                                    {x.author ? x.author[0].toUpperCase() : '?'}
                                </div>
                            )}
                        </div>
                        <div className={styles.details}>
                            <div className={styles.author}>{x.author}</div>
                            <div className={styles.content}>{x.content}</div>
                            <div className={styles.foot}>
                                <div className={styles.time}>{x.time}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ color: '#dc1d08', fontSize: '14px' }}>
                                        {'★'.repeat(x.rating || 5)}
                                    </div>
                                    <button
                                        onClick={() => handleEditReview(x)}
                                        style={{
                                            background: 'none',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            padding: '2px 8px',
                                            fontSize: '12px',
                                            color: '#0f084b',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <Icon name="edit" size="12" />
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    ))
                ) : (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        color: '#666',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px'
                    }}>
                        <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                            No reviews yet
                        </div>
                        <div style={{ fontSize: '14px', color: '#999' }}>
                            Reviews you write about creators will appear here
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Add Review Modal */}
        <AddReviewModal
            isOpen={showAddReviewModal}
            fetchCreators={fetchCreators}
            onClose={() => { setShowAddReviewModal(false); setEditingReview(null); }}
            onSubmit={handleAddReview}
            submitting={submitting}
            editingReview={editingReview}
        />
        </>
    );
};

// Add Review Modal Component
const AddReviewModal = ({ fetchCreators, onClose, onSubmit, submitting, isOpen, editingReview }) => {
    const [searchValue, setSearchValue] = useState('');
    const [selectedCreator, setSelectedCreator] = useState(null);
    const isEditing = !!editingReview;
    const [formData, setFormData] = useState({
        creator_profile_id: '',
        review_text: '',
        review_title: '',
        rating: 5,
        collaboration_month: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    });

    useEffect(() => {
        if (!isOpen) {
            setSearchValue('');
            setSelectedCreator(null);
            setFormData({
                creator_profile_id: '',
                review_text: '',
                review_title: '',
                rating: 5,
                collaboration_month: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            });
        } else if (editingReview) {
            setFormData({
                creator_profile_id: editingReview.creator_profile_id || '',
                review_text: editingReview.review_text || '',
                review_title: editingReview.review_title || '',
                rating: editingReview.rating || 5,
                collaboration_month: editingReview.collaboration_month || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            });
            setSelectedCreator({
                id: editingReview.creator_profile_id,
                first_name: editingReview.creator_name || editingReview.creator_first_name || editingReview.author,
                handle: editingReview.creator_handle
            });
        }
    }, [isOpen, editingReview]);

    const handleCreatorSelect = (creator) => {
        setSelectedCreator(creator);
        setFormData(prev => ({
            ...prev,
            creator_profile_id: creator.id || creator.creator_profile_id
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.creator_profile_id || !formData.review_text) {
            alert('Please select a creator and write a review');
            return;
        }
        onSubmit(formData);
    };

    return (
        <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="500px">
            <div style={{ padding: '24px' }}>
                <h3 style={{ marginTop: 0, color: '#0f084b' }}>{isEditing ? 'Edit Creator Review' : 'Add Creator Review'}</h3>

                <form onSubmit={handleSubmit}>
                    {/* Creator Selection */}
                    {isEditing ? (
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                                Creator
                            </label>
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#f0f7ff',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#0f084b',
                                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span>
                                    {selectedCreator?.handle ? (
                                        <><strong>@{selectedCreator.handle}</strong> - {selectedCreator.first_name || 'Creator'}</>
                                    ) : (
                                        selectedCreator?.first_name || 'Creator'
                                    )}
                                </span>
                            </div>
                        </div>
                    ) : (
                    <div style={{ marginBottom: '20px' }}>
                        <CreatorSearchAutocomplete
                            value={searchValue}
                            onChange={setSearchValue}
                            onSelect={handleCreatorSelect}
                            fetchCreators={fetchCreators}
                            selectedCreator={selectedCreator}
                            label="Select Creator *"
                            placeholder="Search by creator name, handle, or location..."
                            style={{ width: '100%' }}
                        />
                        {selectedCreator && (
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                backgroundColor: '#f0f7ff',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#0f084b',
                                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    {(selectedCreator.avatar_asset_url || selectedCreator.avatar_url) ? (
                                        <img
                                            src={selectedCreator.avatar_asset_url || selectedCreator.avatar_url}
                                            alt=""
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                const initials = `${selectedCreator.first_name?.[0] || ''}${selectedCreator.last_name?.[0] || ''}`.toUpperCase() || '?';
                                                e.target.parentElement.innerHTML = `<span style="font-size: 10px; color: #666;">${initials}</span>`;
                                            }}
                                        />
                                    ) : (
                                        <span style={{ fontSize: '10px', color: '#666' }}>
                                            {`${selectedCreator.first_name?.[0] || ''}${selectedCreator.last_name?.[0] || ''}`.toUpperCase() || '?'}
                                        </span>
                                    )}
                                </div>
                                <span>
                                    ✓ Selected: {selectedCreator.handle ? (
                                        <>
                                            <strong>@{selectedCreator.handle}</strong>
                                            {' - '}
                                            {`${selectedCreator.first_name || ''}${selectedCreator.last_name ? ` ${selectedCreator.last_name[0]}.` : ''}`.trim() || 'Creator'}
                                        </>
                                    ) : (
                                        `${selectedCreator.first_name || ''}${selectedCreator.last_name ? ` ${selectedCreator.last_name[0]}.` : ''}`.trim() || 'Creator'
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Review Title (optional) */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                            Review Title (optional)
                        </label>
                        <input
                            type="text"
                            value={formData.review_title}
                            onChange={(e) => {
                                const value = e.target.value.slice(0, 280);
                                setFormData({ ...formData, review_title: value });
                            }}
                            maxLength={280}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                            placeholder="e.g., Amazing collaboration!"
                        />
                        <div style={{ 
                            fontSize: '12px', 
                            color: formData.review_title.length >= 250 ? '#dc1d08' : '#999',
                            marginTop: '4px',
                            textAlign: 'right'
                        }}>
                            {formData.review_title.length}/280
                        </div>
                    </div>

                    {/* Rating */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                            Rating
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, rating: star })}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: star <= formData.rating ? '#FFD700' : '#e0e0e0'
                                    }}
                                >
                                    ★
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Review Content */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                            Review *
                        </label>
                        <textarea
                            value={formData.review_text}
                            onChange={(e) => {
                                const value = e.target.value.slice(0, 280);
                                setFormData({ ...formData, review_text: value });
                            }}
                            maxLength={280}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '14px',
                                minHeight: '120px',
                                resize: 'vertical'
                            }}
                            placeholder="Share your experience working with this creator..."
                            required
                        />
                        <div style={{ 
                            fontSize: '12px', 
                            color: formData.review_text.length >= 250 ? '#dc1d08' : '#999',
                            marginTop: '4px',
                            textAlign: 'right'
                        }}>
                            {formData.review_text.length}/280
                        </div>
                    </div>

                    {/* Collaboration Month */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: '#333' }}>
                            Collaboration Month
                        </label>
                        <input
                            type="text"
                            value={formData.collaboration_month}
                            onChange={(e) => setFormData({ ...formData, collaboration_month: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                            placeholder="e.g., October 2024"
                        />
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '10px 20px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                background: 'white',
                                color: '#666',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                background: submitting ? '#999' : '#0f084b',
                                color: 'white',
                                fontSize: '14px',
                                cursor: submitting ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {submitting ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Review' : 'Add Review')}
                        </button>
                    </div>
                </form>
            </div>
        </ModalPortal>
    );
};

export default Reviews;
